import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Center,
  Container,
  Group,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconArrowLeft, IconNews } from "@tabler/icons-react";
import { useNewsItem, useNewsList } from "../api/queries/news";
import { usePublicSettings } from "../api/queries/settings";
import type { NewsItem, NewsTag } from "../api/types/news";
import { useLink, usePath } from "../router";
import { formatDate } from "../time/format";
import { useZone } from "../time/useZone";
import { MarkdownContent } from "./MarkdownContent";

const PAGE_SIZE = 20;

export function NewsPage() {
  const path = usePath();
  const { data: settings, isLoading } = usePublicSettings();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Container size="md" py={{ base: "md", sm: "xl" }} px={{ base: "md", sm: "xl" }}>
        <Skeleton height={40} mb="sm" />
        <Skeleton height={20} mb="xl" />
        <Skeleton height={200} />
      </Container>
    );
  }

  if (!settings?.news_enabled) {
    return (
      <Container size="md" py={{ base: "md", sm: "xl" }} px={{ base: "md", sm: "xl" }}>
        <Paper p="xl" withBorder radius="sm" style={{ textAlign: "center" }}>
          <Title order={2} size="h3" mb="xs">
            {t("news.unavailableTitle")}
          </Title>
          <Text c="dimmed">{t("news.unavailableBody")}</Text>
        </Paper>
      </Container>
    );
  }

  if (path.startsWith("/news/")) {
    const idStr = path.slice("/news/".length);
    const id = parseInt(idStr, 10);
    if (!Number.isNaN(id) && id > 0) {
      return <NewsDetail id={id} />;
    }
  }

  return <NewsList />;
}

function tagColor(name: string): string {
  const lower = name.toLowerCase();
  if (lower === "web") return "blue";
  if (lower === "app") return "teal";
  return "gray";
}

export function TagBadge({ tag }: { tag: NewsTag }) {
  return (
    <Badge color={tagColor(tag.name)} variant="light" size="sm">
      {tag.name}
    </Badge>
  );
}

function NewsCard({ item }: { item: NewsItem }) {
  const { t } = useTranslation();
  const zone = useZone();
  const detailLink = useLink(`/news/${item.id}`);
  const timestamp = item.published_at ? Date.parse(item.published_at) : Date.parse(item.created_at);
  const formattedDate = Number.isNaN(timestamp) ? "" : formatDate(timestamp, zone.name);

  return (
    <Paper p={{ base: "md", sm: "lg" }} withBorder radius="sm">
      <Group justify="space-between" align="center" mb="xs" wrap="wrap" gap="xs">
        <Group gap="xs" align="center" wrap="wrap">
          {item.tags.map((tag) => (
            <TagBadge key={tag.id} tag={tag} />
          ))}
          {item.release_version && (
            <Badge variant="outline" size="sm">
              {item.release_version}
            </Badge>
          )}
        </Group>
        {formattedDate && (
          <Text size="xs" c="dimmed">
            {formattedDate}
          </Text>
        )}
      </Group>

      <Anchor
        href={detailLink.href}
        onClick={detailLink.onClick}
        underline="never"
        c="inherit"
        mb="sm"
        display="block"
      >
        <Title order={2} size="h3" lh={1.3}>
          {item.title}
        </Title>
      </Anchor>

      <Text component="div" mt="sm" lineClamp={4}>
        <MarkdownContent markdown={item.body} />
      </Text>

      <Group justify="flex-end" mt="md">
        <Anchor href={detailLink.href} onClick={detailLink.onClick} size="sm" c="accent">
          {t("news.readMore")}
        </Anchor>
      </Group>
    </Paper>
  );
}

function NewsList() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const offset = (page - 1) * PAGE_SIZE;
  const { data, isLoading, isError } = useNewsList({
    offset,
    limit: PAGE_SIZE,
  });

  return (
    <Container size="md" py={{ base: "md", sm: "xl" }} px={{ base: "md", sm: "xl" }}>
      <Group justify="space-between" align="center" mb="lg" wrap="wrap" gap="md">
        <Group gap="xs" align="center">
          <IconNews size={26} stroke={1.5} aria-hidden="true" />
          <Title order={1} size="h2">
            {t("news.title")}
          </Title>
        </Group>
      </Group>

      {isLoading && (
        <Stack gap="md">
          <Skeleton height={140} radius="sm" />
          <Skeleton height={140} radius="sm" />
          <Skeleton height={140} radius="sm" />
        </Stack>
      )}

      {isError && (
        <Alert color="red" variant="light" my="md">
          {t("news.errorLoading")}
        </Alert>
      )}

      {!isLoading && !isError && data && data.items.length === 0 && (
        <Paper p="xl" withBorder radius="sm" style={{ textAlign: "center" }}>
          <Text c="dimmed">{t("news.empty")}</Text>
        </Paper>
      )}

      {!isLoading && !isError && data && data.items.length > 0 && (
        <Stack gap="md">
          {data.items.map((item) => (
            <NewsCard key={item.id} item={item} />
          ))}
        </Stack>
      )}

      {data && data.total > PAGE_SIZE && (
        <Center mt="xl">
          <Pagination
            total={Math.ceil(data.total / PAGE_SIZE)}
            value={page}
            onChange={setPage}
            size="sm"
          />
        </Center>
      )}
    </Container>
  );
}

function NewsDetail({ id }: { id: number }) {
  const { t } = useTranslation();
  const zone = useZone();
  const backLink = useLink("/news");
  const { data: item, isLoading, isError } = useNewsItem(id);

  if (isLoading) {
    return (
      <Container size="md" py={{ base: "md", sm: "xl" }} px={{ base: "md", sm: "xl" }}>
        <Skeleton height={24} width={120} mb="lg" />
        <Skeleton height={36} mb="sm" />
        <Skeleton height={20} width={200} mb="xl" />
        <Skeleton height={300} />
      </Container>
    );
  }

  if (isError || !item) {
    return (
      <Container size="md" py={{ base: "md", sm: "xl" }} px={{ base: "md", sm: "xl" }}>
        <Anchor
          href={backLink.href}
          onClick={backLink.onClick}
          display="inline-flex"
          style={{ alignItems: "center", gap: 6 }}
          mb="lg"
        >
          <IconArrowLeft size={16} aria-hidden="true" />
          {t("news.backToList")}
        </Anchor>

        <Paper p="xl" withBorder radius="sm">
          <Title order={2} size="h3" mb="xs">
            {t("news.notFoundTitle")}
          </Title>
          <Text c="dimmed">{t("news.notFoundBody")}</Text>
        </Paper>
      </Container>
    );
  }

  const timestamp = item.published_at ? Date.parse(item.published_at) : Date.parse(item.created_at);
  const formattedDate = Number.isNaN(timestamp) ? "" : formatDate(timestamp, zone.name);

  return (
    <Container size="md" py={{ base: "md", sm: "xl" }} px={{ base: "md", sm: "xl" }}>
      <Anchor
        href={backLink.href}
        onClick={backLink.onClick}
        display="inline-flex"
        style={{ alignItems: "center", gap: 6 }}
        mb="lg"
      >
        <IconArrowLeft size={16} aria-hidden="true" />
        {t("news.backToList")}
      </Anchor>

      <Paper p={{ base: "md", sm: "xl" }} withBorder radius="sm">
        <Group justify="space-between" align="center" mb="sm" wrap="wrap" gap="xs">
          <Group gap="xs" align="center" wrap="wrap">
            {item.tags.map((tag) => (
              <TagBadge key={tag.id} tag={tag} />
            ))}
            {item.release_version && (
              <Badge variant="outline" size="sm">
                {item.release_version}
              </Badge>
            )}
          </Group>
          {formattedDate && (
            <Text size="xs" c="dimmed">
              {formattedDate}
            </Text>
          )}
        </Group>

        <Title order={1} size="h2" mb="lg">
          {item.title}
        </Title>

        <Box mt="md">
          <MarkdownContent markdown={item.body} />
        </Box>
      </Paper>
    </Container>
  );
}
